import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { trackPageView } from "./useGoogleAnalytics";

// Generate a simple session ID for grouping page views
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("visitor_session_id");
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem("visitor_session_id", sessionId);
  }
  return sessionId;
};

// Cache location data in session to avoid repeated API calls
const getCachedLocation = () => {
  const cached = sessionStorage.getItem("visitor_location");
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {
      return null;
    }
  }
  return null;
};

const setCachedLocation = (location: {
  country: string;
  countryCode: string;
  city: string;
  region: string;
}) => {
  sessionStorage.setItem("visitor_location", JSON.stringify(location));
};

export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view in Google Analytics 4
    trackPageView(location.pathname);

    const trackSupabasePageView = async () => {
      try {
        // Check for cached location first
        let geoLocation = getCachedLocation();

        // If no cached location, fetch from edge function
        if (!geoLocation) {
          try {
            const { data, error } = await supabase.functions.invoke("get-visitor-location");
            if (!error && data?.location) {
              geoLocation = data.location;
              setCachedLocation(geoLocation);
            }
          } catch (geoError) {
            console.error("Failed to get geolocation:", geoError);
          }
        }

        await supabase.from("page_views").insert({
          page_path: location.pathname,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null,
          session_id: getSessionId(),
          country: geoLocation?.country || null,
          country_code: geoLocation?.countryCode || null,
          city: geoLocation?.city || null,
          region: geoLocation?.region || null,
        });
      } catch (error) {
        // Silently fail - don't impact user experience
        console.error("Failed to track page view:", error);
      }
    };

    trackSupabasePageView();
  }, [location.pathname]);
};
